/**
 * Connect the label printer and print, in one place.
 *
 * Three screens print labels now - a binder, the collection and the end of a
 * vendor deal - and each had its own copy of the same twenty lines: are we
 * connected, connect if not, read the saved settings, print, count the labels,
 * say what went wrong. A fourth copy is how one screen ends up passing no
 * size and printing half a roll blank.
 *
 * The state it returns is what a print bar draws: linked, linking, printing,
 * how many are done, and the last error. `run(cards)` connects when it has to
 * and prints with the settings the print screen saved, so a caller never has
 * to know whether the printer is awake.
 */

import { useCallback, useState } from 'react';

import {
  isConnected, connect, printLabels, printerModel, rememberedPrinter,
  readPrefs as labelPrefs, printingSupported,
} from '../services/labelPrinter';
import haptics from '../utils/haptics';

export default function usePrintLabels({ qrUrlFor = null } = {}) {
  const [state, setState] = useState(() => ({
    linked: isConnected(), linking: false, printing: false, done: 0, error: null,
  }));

  const run = useCallback(async (cards) => {
    if (!cards || cards.length === 0) return false;
    setState((p) => ({ ...p, error: null }));
    try {
      if (!isConnected()) {
        setState((p) => ({ ...p, linking: true }));
        await connect();
        setState((p) => ({ ...p, linked: true }));
      }
    } catch (err) {
      setState((p) => ({
        ...p, linking: false, linked: false, error: err?.message || 'Could not reach the printer.',
      }));
      return false;
    }
    setState((p) => ({ ...p, linking: false, linked: true, printing: true, done: 0 }));
    try {
      // The settings the print screen saved. Passing no size lets the roll
      // fall back to one label and feed the rest out blank.
      const { fields, size } = labelPrefs();
      await printLabels(cards, {
        fields,
        size,
        qrUrlFor,
        onProgress: (n) => setState((p) => ({ ...p, done: n })),
      });
      haptics.success();
      return true;
    } catch (err) {
      setState((p) => ({ ...p, error: err?.message || 'The print stopped.' }));
      return false;
    } finally {
      setState((p) => ({ ...p, printing: false }));
    }
  }, [qrUrlFor]);

  return {
    ...state,
    run,
    supported: printingSupported(),
    printerName: rememberedPrinter()?.name || printerModel(),
    reset: () => setState((p) => ({ ...p, error: null, done: 0 })),
    // Asked on the way into a print screen: a link made earlier in the
    // session is still good, and opening on "Connect and print" would ask for
    // a step already taken.
    refresh: () => setState((p) => ({ ...p, linked: isConnected(), error: null })),
  };
}
